import { Tags } from "@/enum/tags";
import { toQueryParams } from "@/utils/to-query-params";

import { apiSlice } from "../http";
import {
  PendingExpired,
  PendingExpiredFilter,
  PendingItemsExportFilter,
  PendingItemsFilter,
  PendingItemsResponse,
  PendingNetworkFilter,
  PendingNetworkResponse,
  PendingStoreFilter,
  PendingStoreResponse,
  PendingWindow,
  PriceRemarkExportResult,
} from "./types";

const NO_CONTENT = 204;

async function exportRequestHandler(
  response: Response,
): Promise<PriceRemarkExportResult> {
  if (response.status === NO_CONTENT) return null;
  return response.json();
}

function networkParams(filter: PendingNetworkFilter): string {
  return toQueryParams({
    labelType: filter.labelType,
    refresh: filter.refresh ? "true" : undefined,
  });
}

function storeParams(filter: PendingStoreFilter): string {
  return toQueryParams({
    labelType: filter.labelType,
    refresh: filter.refresh ? "true" : undefined,
    storeCode: filter.storeCode,
  });
}

function itemsParams(filter: PendingItemsExportFilter): string {
  return toQueryParams({
    labelType: filter.labelType,
    refresh: filter.refresh ? "true" : undefined,
    storeCode: filter.storeCode,
    batch: filter.batch,
    status: filter.status,
    sortBy: filter.sortBy,
    sortOrder: filter.sortOrder,
  });
}

export const priceRemarkPendingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPriceRemarkPendingWindow: builder.query<PendingWindow, void>({
      query: () => ({ url: "/price-remark/pending/window", method: "GET" }),
      providesTags: [Tags.PRICE_REMARK_PENDING],
    }),
    getPriceRemarkPendingNetwork: builder.query<
      PendingNetworkResponse,
      PendingNetworkFilter
    >({
      query: (filter) => ({
        url: `/price-remark/pending/network?${networkParams(filter)}`,
        method: "GET",
        timeout: 60000,
      }),
      providesTags: [Tags.PRICE_REMARK_PENDING],
    }),
    getPriceRemarkPendingExpired: builder.query<
      PendingExpired,
      PendingExpiredFilter
    >({
      query: (filter) => ({
        url: `/price-remark/pending/expired?${toQueryParams({
          labelType: filter.labelType,
          refresh: filter.refresh ? "true" : undefined,
          storeCode: filter.storeCode,
        })}`,
        method: "GET",
        timeout: 90000,
      }),
      providesTags: [Tags.PRICE_REMARK_PENDING],
    }),
    getPriceRemarkPendingStore: builder.query<
      PendingStoreResponse,
      PendingStoreFilter
    >({
      query: (filter) => ({
        url: `/price-remark/pending/store?${storeParams(filter)}`,
        method: "GET",
        timeout: 60000,
      }),
      providesTags: [Tags.PRICE_REMARK_PENDING],
    }),
    getPriceRemarkPendingItems: builder.query<
      PendingItemsResponse,
      PendingItemsFilter
    >({
      query: (filter) => ({
        url: `/price-remark/pending/items?${itemsParams(filter)}&${toQueryParams(
          { page: filter.page, perPage: filter.perPage },
        )}`,
        method: "GET",
        timeout: 60000,
      }),
      transformResponse: (response: PendingItemsResponse) =>
        response || { data: [], total: 0 },
      providesTags: [Tags.PRICE_REMARK_PENDING],
    }),
    exportPriceRemarkPendingNetwork: builder.mutation<
      PriceRemarkExportResult,
      PendingNetworkFilter
    >({
      query: (filter) => ({
        url: `/price-remark/pending/network/export?${networkParams(filter)}`,
        method: "POST",
        responseHandler: exportRequestHandler,
      }),
    }),
    exportPriceRemarkPendingItems: builder.mutation<
      PriceRemarkExportResult,
      PendingItemsExportFilter
    >({
      query: (filter) => ({
        url: `/price-remark/pending/items/export?${itemsParams(filter)}`,
        method: "POST",
        responseHandler: exportRequestHandler,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetPriceRemarkPendingWindowQuery,
  useGetPriceRemarkPendingNetworkQuery,
  useGetPriceRemarkPendingExpiredQuery,
  useGetPriceRemarkPendingStoreQuery,
  useGetPriceRemarkPendingItemsQuery,
  useExportPriceRemarkPendingNetworkMutation,
  useExportPriceRemarkPendingItemsMutation,
} = priceRemarkPendingApi;
